import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { money } from "../lib/data";

const TABS = ["SETTINGS", "ORDERS", "SUBSCRIBERS"];
const ORDER_STATUSES = ["paid", "production", "shipped", "delivered", "cancelled", "refunded"];

const toLocal = (iso) => (iso ? iso.slice(0, 16) : "");
const toIso = (local) => new Date(local + (local.length === 16 ? ":00" : "")).toISOString();

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      sessionStorage.setItem("ll_admin", data.token);
      onLogin(data.token);
    } catch {
      setError("Invalid password");
    }
  };
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-sm flex-col justify-center px-5 pt-24">
      <p className="font-tech text-[11px] tracking-[0.3em] text-ll-red">OWNER ONLY</p>
      <h1 className="mt-2 font-display text-5xl tracking-wide">ADMIN</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input data-testid="admin-password-input" type="password" value={password}
               onChange={(e) => setPassword(e.target.value)} placeholder="Password"
               className="w-full border border-ll-linestrong bg-ll-black px-4 py-3 text-sm placeholder:text-ll-steel/60 outline-none focus:border-ll-red" />
        {error && <p data-testid="admin-login-error" className="text-xs text-ll-red">{error}</p>}
        <motion.button data-testid="admin-login-button" whileTap={{ scale: 0.98 }} type="submit"
                       className="w-full bg-ll-red py-3 font-tech text-sm tracking-[0.25em] hover:bg-ll-redhover">
          SIGN IN
        </motion.button>
      </form>
    </div>
  );
}

function Settings({ headers }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/settings", { headers }).then((r) => setS(r.data)); }, [headers]);
  if (!s) return <p className="font-tech text-sm text-ll-steel">Loading…</p>;

  const save = async () => {
    setMsg("");
    try {
      await api.put("/admin/settings", {
        open_at: toIso(s.open_at_local), close_at: toIso(s.close_at_local),
        ship_start: toIso(s.ship_start_local), ship_end: toIso(s.ship_end_local),
        preorder_price: Number(s.preorder_price), retail_price: Number(s.retail_price),
        shipping_flat_rate: Number(s.shipping_flat_rate ?? 6.99),
        status: s.status, max_qty: s.max_qty ? Number(s.max_qty) : null, notice: s.notice,
      }, { headers });
      setMsg("Settings saved.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Save failed.");
    }
  };

  const state = { ...s, open_at_local: s.open_at_local ?? toLocal(s.open_at), close_at_local: s.close_at_local ?? toLocal(s.close_at),
                  ship_start_local: s.ship_start_local ?? toLocal(s.ship_start), ship_end_local: s.ship_end_local ?? toLocal(s.ship_end) };
  const set = (k, v) => setS({ ...state, [k]: v });

  const Field = ({ label, k, type = "text", testid }) => (
    <div>
      <label className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">{label}</label>
      <input data-testid={testid} type={type} value={state[k] ?? ""} onChange={(e) => set(k, e.target.value)}
             className="mt-1 w-full border border-ll-linestrong bg-ll-black px-3 py-2 text-sm outline-none focus:border-ll-red" />
    </div>
  );

  return (
    <div data-testid="admin-settings" className="max-w-xl space-y-4">
      <p className="font-tech text-[11px] text-ll-steel">All dates/times are stored in UTC. The countdown uses the closing time below — one fixed deadline for every visitor.</p>
      <Field label="PREORDER OPENS (UTC)" k="open_at_local" type="datetime-local" testid="admin-open-at" />
      <Field label="PREORDER CLOSES (UTC)" k="close_at_local" type="datetime-local" testid="admin-close-at" />
      <Field label="ESTIMATED SHIPPING START (UTC)" k="ship_start_local" type="datetime-local" testid="admin-ship-start" />
      <Field label="ESTIMATED SHIPPING END (UTC)" k="ship_end_local" type="datetime-local" testid="admin-ship-end" />
      <div className="grid grid-cols-3 gap-4">
        <Field label="PREORDER PRICE" k="preorder_price" type="number" testid="admin-preorder-price" />
        <Field label="FUTURE RETAIL PRICE" k="retail_price" type="number" testid="admin-retail-price" />
        <Field label="FLAT SHIPPING" k="shipping_flat_rate" type="number" testid="admin-shipping-rate" />
      </div>
      <Field label="MAX QUANTITY (BLANK = UNLIMITED)" k="max_qty" type="number" testid="admin-max-qty" />
      <div>
        <label className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">PREORDER STATUS</label>
        <div className="mt-2 flex gap-2">
          {["open", "closed"].map((st) => (
            <button key={st} data-testid={`admin-status-${st}`} onClick={() => set("status", st)}
                    className={`border px-6 py-2 font-tech text-xs tracking-[0.2em] ${state.status === st ? "border-ll-red bg-ll-red" : "border-ll-linestrong"}`}>
              {st.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">PRODUCT NOTICE ({"{CLOSE_DATE}"} and {"{SHIP_RANGE}"} auto-filled)</label>
        <textarea data-testid="admin-notice" rows={3} value={state.notice ?? ""} onChange={(e) => set("notice", e.target.value)}
                  className="mt-1 w-full border border-ll-linestrong bg-ll-black px-3 py-2 text-sm outline-none focus:border-ll-red" />
      </div>
      {msg && <p data-testid="admin-settings-msg" className="font-tech text-xs text-green-400">{msg}</p>}
      <motion.button data-testid="admin-save-settings" whileTap={{ scale: 0.98 }} onClick={save}
                     className="bg-ll-red px-10 py-3 font-tech text-sm tracking-[0.25em] hover:bg-ll-redhover">
        SAVE SETTINGS
      </motion.button>
    </div>
  );
}

function Orders({ headers }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [nudgeMsg, setNudgeMsg] = useState("");
  const load = (q = "") => api.get("/admin/orders", { headers, params: q ? { search: q } : {} }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [headers]);
  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const nudge = async () => {
    setNudgeMsg("Sending…");
    try {
      const r = await api.post("/admin/nudge-abandoned", {}, { headers });
      setNudgeMsg(`${r.data.nudged} nudge email(s) sent to unpaid checkouts.`);
      load(search);
    } catch {
      setNudgeMsg("Nudge failed.");
    }
  };

  const updateStatus = async (num, status) => {
    await api.patch(`/admin/orders/${num}`, { status }, { headers });
    load(search);
  };

  const download = async (path, name) => {
    const r = await api.get(path, { headers, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="admin-order-search" type="text" value={search} placeholder="Search order #, name or email"
               onChange={(e) => setSearch(e.target.value)}
               className="w-72 border border-ll-linestrong bg-ll-black px-3 py-2 text-sm placeholder:text-ll-steel/60 outline-none focus:border-ll-red" />
        <button data-testid="admin-export-orders" onClick={() => download("/admin/orders-export", "eternel-orders.csv")}
                className="border border-ll-linestrong px-4 py-2 font-tech text-xs tracking-[0.2em] hover:border-ll-red hover:text-ll-red">
          EXPORT CSV
        </button>
        <button data-testid="admin-nudge-abandoned" onClick={nudge}
                className="border border-ll-linestrong px-4 py-2 font-tech text-xs tracking-[0.2em] hover:border-ll-red hover:text-ll-red">
          NUDGE UNPAID CHECKOUTS
        </button>
        {nudgeMsg && <span data-testid="admin-nudge-msg" className="font-tech text-xs text-green-400">{nudgeMsg}</span>}
      </div>
      <div className="mt-6 overflow-x-auto">
        <table data-testid="admin-orders-table" className="w-full min-w-[900px] font-tech text-xs">
          <thead>
            <tr className="border-b border-ll-line text-left text-ll-steel">
              <th className="py-2 pr-4">ORDER</th><th className="py-2 pr-4">DATE</th><th className="py-2 pr-4">CUSTOMER</th>
              <th className="py-2 pr-4">ITEMS</th><th className="py-2 pr-4">TOTAL</th><th className="py-2 pr-4">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_number} className="border-b border-ll-line/50" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="py-3 pr-4">{o.order_number}</td>
                <td className="py-3 pr-4">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="py-3 pr-4">{o.customer.name}<br /><span className="text-ll-steel">{o.customer.email}</span>
                  {o.shipping_address && (
                    <span data-testid={`admin-order-address-${o.order_number}`} className="mt-1 block text-xs leading-relaxed text-ll-bone/80">
                      SHIP TO: {[o.shipping_address.line1, o.shipping_address.line2, [o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code].filter(Boolean).join(" "), o.shipping_address.country].filter(Boolean).join(", ")}
                    </span>
                  )}
                </td>
                <td className="py-3 pr-4">{o.items.map((i) => `${i.name || i.title}×${i.quantity}`).join(", ")}</td>
                <td className="py-3 pr-4">{o.amount_total ? money(o.amount_total) : money(o.subtotal)}</td>
                <td className="py-3 pr-4">
                  {o.status === "pending_payment" || o.status === "payment_failed" ? (
                    <span className="text-ll-steel">{o.status.replace("_", " ").toUpperCase()}</span>
                  ) : (
                    <select data-testid={`admin-order-status-${o.order_number}`} value={o.status}
                            onChange={(e) => updateStatus(o.order_number, e.target.value)}
                            className="border border-ll-linestrong bg-ll-black px-2 py-1">
                      {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                    </select>
                  )}
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-ll-steel">No orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Manufacturing({ headers }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/manufacturing-summary", { headers }).then((r) => setData(r.data)); }, [headers]);
  if (!data) return <p className="font-tech text-sm text-ll-steel">Loading…</p>;
  return (
    <div data-testid="admin-manufacturing">
      <p className="font-tech text-[11px] text-ll-steel">PAID ORDERS ONLY — SEND TO MANUFACTURER AFTER PREORDERS CLOSE</p>
      <div className="mt-4 overflow-x-auto">
        <table data-testid="manufacturing-table" className="w-full min-w-[640px] font-tech text-xs">
          <thead>
            <tr className="border-b border-ll-line text-left text-ll-steel">
              <th className="py-2 pr-4">COLOR</th>
              {data.sizes.map((s) => <th key={s} className="py-2 pr-4">{s}</th>)}
              <th className="py-2">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.colorway} className="border-b border-ll-line/50">
                <td className="py-3 pr-4">{r.colorway}</td>
                {data.sizes.map((s) => <td key={s} className="py-3 pr-4">{r[s]}</td>)}
                <td className="py-3">{r.total}</td>
              </tr>
            ))}
            <tr className="text-ll-red">
              <td className="py-3 pr-4">TOTAL</td>
              {data.sizes.map((s) => <td key={s} className="py-3 pr-4">{data.rows.reduce((a, r) => a + r[s], 0)}</td>)}
              <td className="py-3">{data.grand_total}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Subscribers({ headers }) {
  const download = async () => {
    const r = await api.get("/admin/subscribers-export", { headers, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "eternel-subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="admin-subscribers">
      <p className="max-w-lg font-tech text-[11px] leading-relaxed text-ll-steel">
        Export the drop list with consent flags and consent timestamps. Only contact subscribers through the channels they opted into.
      </p>
      <button data-testid="admin-export-subscribers" onClick={download}
              className="mt-6 border border-ll-linestrong px-6 py-3 font-tech text-xs tracking-[0.2em] hover:border-ll-red hover:text-ll-red">
        EXPORT SUBSCRIBERS CSV
      </button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("ll_admin") || "");
  const [tab, setTab] = useState("SETTINGS");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 md:px-8 md:pt-40">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-6xl tracking-wide">ADMIN</h1>
        <button data-testid="admin-logout" onClick={() => { sessionStorage.removeItem("ll_admin"); setToken(""); }}
                className="font-tech text-xs tracking-[0.2em] text-ll-steel hover:text-ll-red">
          SIGN OUT
        </button>
      </div>
      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                  className={`border px-5 py-2 font-tech text-xs tracking-[0.2em] ${tab === t ? "border-ll-red bg-ll-red" : "border-ll-linestrong text-ll-steel hover:text-ll-bone"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-10">
        {tab === "SETTINGS" && <Settings headers={headers} />}
        {tab === "ORDERS" && <Orders headers={headers} />}
        {tab === "MANUFACTURING" && <Manufacturing headers={headers} />}
        {tab === "SUBSCRIBERS" && <Subscribers headers={headers} />}
      </div>
    </div>
  );
}
